"use client";
import { Camera } from "lucide-react";
import { API_ENDPOINTS } from "../../../constants/config";
import { useState, useEffect } from "react";
import axiosInstance from "../../../utils/axiosInstance";
import { toast } from "react-hot-toast";
import { extractApiErrorMessage } from "../../../utils/error-handler";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Checkbox } from "@/components/ui/checkbox";

const CreateTeacherModal = ({ isOpen, onClose, subjects, fetchTeachers, currentAcademicYear }) => {
  const [formData, setFormData] = useState({
    profileImage: null,
    firstName: "",
    middleName: "",
    lastName: "",
    email: "",
    role: "teacher",
    birthday: "",
    employeeCode: "",
    aadhaarCard: "",
    bloodGroup: "",
    gender: "",
    address: {
      street: "",
      city: "",
      state: "",
      pincode: "",
      country: "India",
    },
    subjects: [],
    classAssignments: [],
    status: "active",
    phoneNumber: "",
    qualification: "",
    experience: "",
    motherName: "",
    fatherName: "",
    dateOfJoining: "",
    maritalStatus: "",
    panNumber: "",
    epf: "",
    basicSalary: "",
    allowance: "",
    teacherType: "",
    contractType: "",
    bankAccountDetails: {
      accountHolderName: "",
      accountNumber: "",
      bankName: "",
      ifsc: "",
    },
  });

  const [fieldErrors, setFieldErrors] = useState({});

  // Determine prefix for employee codes based on academic year
  const getEmployeeCodePrefixYear = () => {
    if (currentAcademicYear && currentAcademicYear.startYear) {
      return currentAcademicYear.startYear.toString();
    }
    return new Date().getFullYear().toString();
  };

  // Real-time validation function
  const validateField = (name, value) => {
    const errors = { ...fieldErrors };
    
    switch (name) {
      case "firstName":
        if (!value || value.trim() === "") {
          errors.firstName = "First name is required";
        } else if (value.length < 2) {
          errors.firstName = "First name must be at least 2 characters long";
        } else {
          delete errors.firstName;
        }
        break;
        
      case "lastName":
        // Optional: allow empty, space, or placeholder (e.g. "-") for no last name
        delete errors.lastName;
        break;
        
      case "email":
        // Email is optional; if provided validate format
        if (value && value.trim() !== "") {
          const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
          if (!emailRegex.test(value)) {
            errors.email = "Please enter a valid email address";
          } else {
            delete errors.email;
          }
        } else {
          delete errors.email;
        }
        break;
        
      case "phoneNumber":
        // Phone number is optional, but if provided, validate format
        if (value && value.trim() !== "") {
          const phoneRegex = /^[0-9]{10}$/;
          if (!phoneRegex.test(value.replace(/\s/g, ""))) {
            errors.phoneNumber = "Phone number must be exactly 10 digits";
          } else {
            delete errors.phoneNumber;
          }
        } else {
          delete errors.phoneNumber;
        }
        break;
        
      case "employeeCode":
        if (!value || value.trim() === "") {
          errors.employeeCode = "Employee code is required";
        } else {
          delete errors.employeeCode;
        }
        break;
        
      case "qualification":
        // Qualification is optional
        delete errors.qualification;
        break;
        
      case "experience":
        // Experience is optional, but if provided validate range
        if (value && value.toString().trim() !== "") {
          const expNum = parseFloat(value);
          if (isNaN(expNum) || expNum < 0 || expNum > 50) {
            errors.experience = "Experience must be a valid number between 0 and 50 years";
          } else {
            delete errors.experience;
          }
        } else {
          delete errors.experience;
        }
        break;
        
      case "birthday":
        // Optional; if provided validate age range
        if (value) {
          const birthDate = new Date(value);
          const today = new Date();
          const age = today.getFullYear() - birthDate.getFullYear();
          if (age < 15 || age > 100) {
            errors.birthday = "Teacher age must be between 15 and 100 years";
          } else {
            delete errors.birthday;
          }
        } else {
          delete errors.birthday;
        }
        break;
        
      case "bloodGroup":
        // Blood group is optional
        delete errors.bloodGroup;
        break;
        
      case "address.pincode":
        // Address optional; if pincode provided validate format
        if (value && value.trim() !== "") {
          const pincodeRegex = /^[0-9]{6}$/;
          if (!pincodeRegex.test(value)) {
            errors.pincode = "Pincode must be exactly 6 digits";
          } else {
            delete errors.pincode;
          }
        } else {
          delete errors.pincode;
        }
        break;
        
      default:
        break;
    }
    
    setFieldErrors(errors);
  };

  const generateEmployeeId = async () => {
    try {
      const token = localStorage.getItem("token");
      // Get all teachers to determine the next sequential number
      const teachersResponse = await axiosInstance.get(
        API_ENDPOINTS.usersByRole("teacher"),
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      const teachers = teachersResponse.data.data.users;
      let maxSequentialNumber = 0;

      const prefixYear = getEmployeeCodePrefixYear();

      // Find the highest sequential number for the current prefix year
      teachers.forEach((teacher) => {
        if (teacher.employeeCode) {
          // Expect format: YYYYT001 or numeric; support both by checking prefix
          const code = String(teacher.employeeCode);
          const yearPart = code.substring(0, 4);
          if (yearPart === prefixYear) {
            const sequentialPart = parseInt(code.substring(5), 10);
            if (!isNaN(sequentialPart)) {
              maxSequentialNumber = Math.max(maxSequentialNumber, sequentialPart);
            }
          }
        }
      });

      // Generate next sequential number
      const nextNumber = (maxSequentialNumber + 1).toString().padStart(3, "0");

      // Format: YYYYT001 (using academic year start year and T for Teacher)
      return `${prefixYear}T${nextNumber}`;
    } catch (error) {
      console.error("Error generating employee ID:", error);
      // Fallback using current year for prefix
      const currentYear = new Date().getFullYear().toString();
      const randomNum = Math.floor(Math.random() * 999)
        .toString()
        .padStart(3, "0");
      return `${currentYear}T${randomNum}`;
    }
  };

  const handleChange = async (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    
    // Call real-time validation for the changed field
    validateField(name, value);
  };

  const handleAddressChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      address: {
        ...prev.address,
        [name.split(".")[1]]: value,
      },
    }));
    
    // Validate address fields
    if (name.split(".")[1] === "pincode") {
      validateField("address.pincode", value);
    }
  };

  const handleBankDetailChange = (e) => {
    const { name, value } = e.target;
    const key = name.replace("bankAccountDetails.", "");
    setFormData((prev) => ({
      ...prev,
      bankAccountDetails: {
        ...prev.bankAccountDetails,
        [key]: value,
      },
    }));
  };

  const handleSubjectToggle = (subjectId) => {
    setFormData((prev) => {
      const currentSubjects = prev.subjects;
      const isSelected = currentSubjects.includes(subjectId);

      return {
        ...prev,
        subjects: isSelected
          ? currentSubjects.filter((id) => id !== subjectId)
          : [...currentSubjects, subjectId],
      };
    });
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    setFormData((prev) => ({
      ...prev,
      profileImage: file,
    }));
  };

  // Clear field errors when modal is closed
  useEffect(() => {
    if (!isOpen) {
      setFieldErrors({});
    }
  }, [isOpen]);

  // Generate employee code as soon as modal opens
  useEffect(() => {
    const setEmployeeCodeOnOpen = async () => {
      if (!isOpen) return;
      if (formData.employeeCode && formData.employeeCode.trim() !== "") return;

      const generatedId = await generateEmployeeId();
      if (generatedId) {
        setFormData((prev) => ({
          ...prev,
          employeeCode: generatedId,
        }));
      }
    };

    setEmployeeCodeOnOpen();
  }, [isOpen, currentAcademicYear]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      // Enhanced frontend validation with specific error messages
      const validationErrors = [];

      // Basic required field validation
      if (!formData.firstName || formData.firstName.trim() === "") {
        validationErrors.push("First name is required");
      } else if (formData.firstName.length < 2) {
        validationErrors.push("First name must be at least 2 characters long");
      }

      // Last name optional (allow empty, space, or placeholder e.g. "-")

      // Email is optional; if provided validate format
      if (formData.email && formData.email.trim() !== "") {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(formData.email)) {
          validationErrors.push("Please enter a valid email address");
        }
      }

      // Phone number is optional; if provided validate format
      if (formData.phoneNumber && formData.phoneNumber.trim() !== "") {
        const phoneRegex = /^[0-9]{10}$/;
        if (!phoneRegex.test(formData.phoneNumber.replace(/\s/g, ""))) {
          validationErrors.push("Phone number must be exactly 10 digits");
        }
      }

      // Teacher-specific validation
      if (!formData.employeeCode || formData.employeeCode.trim() === "") {
        validationErrors.push("Employee code is required");
      }

      // Qualification and experience are optional; if experience provided validate range
      if (formData.experience && formData.experience.toString().trim() !== "") {
        const expNum = parseFloat(formData.experience);
        if (isNaN(expNum) || expNum < 0 || expNum > 50) {
          validationErrors.push("Experience must be a valid number between 0 and 50 years");
        }
      }

      // Birthday optional; if provided validate age range
      if (formData.birthday) {
        const birthDate = new Date(formData.birthday);
        const today = new Date();
        const age = today.getFullYear() - birthDate.getFullYear();
        if (age < 15 || age > 100) {
          validationErrors.push("Teacher age must be between 15 and 100 years");
        }
      }

      // Blood group is optional

      if (!formData.subjects || formData.subjects.length === 0) {
        validationErrors.push("Teacher must have at least one subject");
      }

      // Address optional; if any address field provided, validate pincode format when present
      const hasAnyAddress = formData.address && (
        (formData.address.street && formData.address.street.trim() !== "") ||
        (formData.address.city && formData.address.city.trim() !== "") ||
        (formData.address.state && formData.address.state.trim() !== "") ||
        (formData.address.pincode && formData.address.pincode.trim() !== "")
      );
      if (hasAnyAddress && formData.address.pincode && formData.address.pincode.trim() !== "") {
        const pincodeRegex = /^[0-9]{6}$/;
        if (!pincodeRegex.test(formData.address.pincode)) {
          validationErrors.push("Pincode must be exactly 6 digits");
        }
      }

      // Show frontend validation errors
      if (validationErrors.length > 0) {
        toast.error(`Please fix the following errors:\n${validationErrors.join('\n')}`);
        return;
      }

      const token = localStorage.getItem("token");

      // Create FormData instance for multipart/form-data
      const formDataToSend = new FormData();

      // Append profile image if exists
      if (formData.profileImage) {
        formDataToSend.append("profileImage", formData.profileImage);
      }

      // Prepare data object without the profile image
      const dataToSend = {
        ...formData,
        role: "teacher",
      };
      delete dataToSend.profileImage;

      // Append the rest of the data as JSON
      formDataToSend.append("data", JSON.stringify(dataToSend));

      const response = await axiosInstance.post(
        API_ENDPOINTS.usersCreate,
        formDataToSend,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "multipart/form-data",
          },
        }
      );

      if (response.data.status === "success") {
        toast.success("Teacher created successfully");
        onClose();
        fetchTeachers();
        
        // Clear field errors on successful submission
        setFieldErrors({});
      }
    } catch (error) {
      console.error("Error creating teacher:", error);
      
      // Use the enhanced error handling utility
      const errorMessage = extractApiErrorMessage(error);
      toast.error(errorMessage);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="flex h-[100dvh] w-[100vw] max-w-none flex-col gap-0 overflow-hidden rounded-none border-0 p-0 sm:h-auto sm:max-h-[90vh] sm:w-[calc(100vw-2rem)] sm:max-w-4xl sm:rounded-lg sm:border">
        <DialogHeader className="shrink-0 border-b px-4 py-4 sm:px-6">
          <DialogTitle>Create New Teacher</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="flex min-h-0 flex-1 flex-col">
          <div className="flex-1 overflow-y-auto px-4 py-4 sm:px-6">
            <div className="mb-4 rounded-md bg-blue-50 p-3 text-blue-700">
            <p className="text-sm">
              <strong>Note:</strong> Login credentials will be automatically
              generated and sent to the provided email address.
            </p>
            </div>

            <div className="space-y-4">

              <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
                <div className="mb-2 flex justify-center md:col-span-2 xl:col-span-3">
                  <div className="relative w-28 sm:w-32">
                    <div className="group aspect-square w-full overflow-hidden rounded-full border-2 border-gray-200">
                  {formData.profileImage ? (
                    <img
                      src={
                        URL.createObjectURL(formData.profileImage) ||
                        "/placeholder.svg"
                      }
                      alt="Profile Preview"
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full bg-gray-100 flex items-center justify-center">
                      <svg
                        className="w-12 h-12 text-gray-400"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                        />
                      </svg>
                    </div>
                  )}
                  <label className="absolute inset-0 flex items-center justify-center bg-black/40 opacity-0 group-hover:opacity-100 cursor-pointer transition-opacity">
                    <Camera className="w-8 h-8 text-white" />
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleImageChange}
                      className="hidden"
                    />
                  </label>
                </div>
              </div>
                </div>
            <div>
              <label className="text-sm font-medium">First Name *</label>
              <Input
                name="firstName"
                required
                value={formData.firstName}
                onChange={handleChange}
              />
              {fieldErrors.firstName && (
                <p className="text-red-500 text-xs mt-1">{fieldErrors.firstName}</p>
              )}
            </div>
            <div>
              <label className="text-sm font-medium">Middle Name</label>
              <Input
                name="middleName"
                value={formData.middleName}
                onChange={handleChange}
              />
            </div>
            <div>
              <label className="text-sm font-medium">Last Name (optional)</label>
              <Input
                name="lastName"
                placeholder="e.g. leave blank or use - if no last name"
                value={formData.lastName}
                onChange={handleChange}
              />
              {fieldErrors.lastName && (
                <p className="text-red-500 text-xs mt-1">{fieldErrors.lastName}</p>
              )}
            </div>
              </div>

          <div className="space-y-2">
            <label className="text-sm font-medium">Email</label>
            <Input
              name="email"
              type="email"
              value={formData.email}
              onChange={handleChange}
            />
            {fieldErrors.email && (
              <p className="text-red-500 text-xs mt-1">{fieldErrors.email}</p>
            )}
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium">Employee Code *</label>
            <Input
              name="employeeCode"
              required
              value={formData.employeeCode}
              onChange={handleChange}
            />
            {fieldErrors.employeeCode && (
              <p className="text-red-500 text-xs mt-1">{fieldErrors.employeeCode}</p>
            )}
          </div>
          <div className="space-y-2">
            <label className="text-sm font-medium">Aadhaar Card</label>
            <Input
              name="aadhaarCard"
              value={formData.aadhaarCard}
              onChange={handleChange}
              placeholder="Optional"
            />
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium">Subjects *</label>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" className="w-full justify-start">
                  {formData.subjects.length > 0
                    ? `${formData.subjects.length} subject(s) selected`
                    : "Select Subjects"}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent className="w-56 max-h-60 overflow-y-auto">
                <DropdownMenuLabel>Subjects</DropdownMenuLabel>
                <DropdownMenuSeparator />
                {subjects.map((subject) => (
                  <DropdownMenuItem
                    key={subject._id}
                    onSelect={(e) => {
                      e.preventDefault(); // Prevent dropdown from closing
                    }}
                    className="flex items-center space-x-2"
                  >
                    <Checkbox
                      id={`subject-${subject._id}`}
                      checked={formData.subjects.includes(subject._id)}
                      onCheckedChange={() => handleSubjectToggle(subject._id)}
                    />
                    <label
                      htmlFor={`subject-${subject._id}`}
                      className="cursor-pointer"
                    >
                      {subject.name}
                    </label>
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium">Birthday (optional)</label>
            <Input
              name="birthday"
              type="date"
              value={formData.birthday}
              onChange={handleChange}
              max={new Date().toISOString().split("T")[0]}
            />
            {fieldErrors.birthday && (
              <p className="text-red-500 text-xs mt-1">{fieldErrors.birthday}</p>
            )}
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium">Blood Group</label>
            <select
              name="bloodGroup"
              value={formData.bloodGroup}
              onChange={handleChange}
              className="w-full p-2 border rounded-md"
            >
              <option value="">Select Blood Group</option>
              {["A+", "A-", "B+", "B-", "AB+", "AB-", "O+", "O-"].map(
                (group) => (
                  <option key={group} value={group}>
                    {group}
                  </option>
                )
              )}
            </select>
            {fieldErrors.bloodGroup && (
              <p className="text-red-500 text-xs mt-1">{fieldErrors.bloodGroup}</p>
            )}
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium">Gender</label>
            <select
              name="gender"
              value={formData.gender}
              onChange={handleChange}
              className="w-full p-2 border rounded-md"
            >
              <option value="">Select Gender</option>
              {["Male", "Female", "Other"].map((gender) => (
                <option key={gender} value={gender}>
                  {gender}
                </option>
              ))}
            </select>
          </div>

              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div className="space-y-2">
              <label className="text-sm font-medium">Phone Number</label>
              <Input
                name="phoneNumber"
                value={formData.phoneNumber}
                onChange={handleChange}
                pattern="[0-9]*"
                maxLength={10}
              />
              {fieldErrors.phoneNumber && (
                <p className="text-red-500 text-xs mt-1">{fieldErrors.phoneNumber}</p>
              )}
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">Qualification</label>
              <Input
                name="qualification"
                value={formData.qualification}
                onChange={handleChange}
              />
              {fieldErrors.qualification && (
                <p className="text-red-500 text-xs mt-1">{fieldErrors.qualification}</p>
              )}
            </div>
              </div>

              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div className="space-y-2">
              <label className="text-sm font-medium">
                Experience (in years)
              </label>
              <Input
                name="experience"
                type="number"
                value={formData.experience}
                onChange={handleChange}
                min="0"
                step="0.5"
              />
              {fieldErrors.experience && (
                <p className="text-red-500 text-xs mt-1">{fieldErrors.experience}</p>
              )}
            </div>
              </div>

              <div className="border-t pt-4 mt-4">
            <p className="text-sm font-semibold text-gray-700 mb-3">Additional Details</p>
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <div>
                <label className="text-sm font-medium">Mother&apos;s Name</label>
                <Input name="motherName" value={formData.motherName} onChange={handleChange} />
              </div>
              <div>
                <label className="text-sm font-medium">Father&apos;s Name</label>
                <Input name="fatherName" value={formData.fatherName} onChange={handleChange} />
              </div>
              <div>
                <label className="text-sm font-medium">Date of Joining</label>
                <Input name="dateOfJoining" type="date" value={formData.dateOfJoining} onChange={handleChange} />
              </div>
              <div>
                <label className="text-sm font-medium">Marital Status</label>
                <select name="maritalStatus" value={formData.maritalStatus} onChange={handleChange} className="w-full p-2 border rounded-md">
                  <option value="">Select</option>
                  <option value="Single">Single</option>
                  <option value="Married">Married</option>
                  <option value="Divorced">Divorced</option>
                  <option value="Widowed">Widowed</option>
                </select>
              </div>
              <div>
                <label className="text-sm font-medium">PAN Number (optional)</label>
                <Input name="panNumber" value={formData.panNumber} onChange={handleChange} placeholder="Optional" />
              </div>
              <div>
                <label className="text-sm font-medium">Teacher Type</label>
                <select name="teacherType" value={formData.teacherType} onChange={handleChange} className="w-full p-2 border rounded-md">
                  <option value="">Select</option>
                  <option value="PRT">PRT</option>
                  <option value="TGT">TGT</option>
                  <option value="PGT">PGT</option>
                </select>
              </div>
              <div>
                <label className="text-sm font-medium">Contract Type</label>
                <Input name="contractType" value={formData.contractType} onChange={handleChange} placeholder="e.g. Permanent, Contract" />
              </div>
            </div>
              </div>

              <div className="border-t pt-4 mt-4">
            <p className="text-sm font-semibold text-gray-700 mb-3">Salary Details</p>
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <div>
                <label className="text-sm font-medium">EPF</label>
                <Input name="epf" value={formData.epf} onChange={handleChange} />
              </div>
              <div>
                <label className="text-sm font-medium">Basic Salary</label>
                <Input name="basicSalary" type="number" min="0" value={formData.basicSalary} onChange={handleChange} />
              </div>
              <div>
                <label className="text-sm font-medium">Allowance</label>
                <Input name="allowance" type="number" min="0" value={formData.allowance} onChange={handleChange} />
              </div>
            </div>
              </div>

              <div className="border-t pt-4 mt-4">
            <p className="text-sm font-semibold text-gray-700 mb-3">Bank Account Details</p>
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <div>
                <label className="text-sm font-medium">Account Holder Name</label>
                <Input name="bankAccountDetails.accountHolderName" value={formData.bankAccountDetails?.accountHolderName || ""} onChange={handleBankDetailChange} />
              </div>
              <div>
                <label className="text-sm font-medium">Account Number</label>
                <Input name="bankAccountDetails.accountNumber" value={formData.bankAccountDetails?.accountNumber || ""} onChange={handleBankDetailChange} />
              </div>
              <div>
                <label className="text-sm font-medium">Bank Name</label>
                <Input name="bankAccountDetails.bankName" value={formData.bankAccountDetails?.bankName || ""} onChange={handleBankDetailChange} />
              </div>
              <div>
                <label className="text-sm font-medium">IFSC</label>
                <Input name="bankAccountDetails.ifsc" value={formData.bankAccountDetails?.ifsc || ""} onChange={handleBankDetailChange} />
              </div>
            </div>
              </div>

              <div>
            <label className="text-sm font-medium">Address (optional)</label>
            <div className="space-y-2">
              <Input
                name="address.street"
                placeholder="Street"
                value={formData.address.street}
                onChange={handleAddressChange}
              />
              <Input
                name="address.city"
                placeholder="City"
                value={formData.address.city}
                onChange={handleAddressChange}
              />
              <Input
                name="address.state"
                placeholder="State"
                value={formData.address.state}
                onChange={handleAddressChange}
              />
              <Input
                name="address.pincode"
                placeholder="Pincode"
                value={formData.address.pincode}
                onChange={handleAddressChange}
                maxLength={6}
                pattern="[0-9]*"
              />
              {fieldErrors.pincode && (
                <p className="text-red-500 text-xs mt-1">{fieldErrors.pincode}</p>
              )}
            </div>
              </div>
            </div>
          </div>

          <DialogFooter className="shrink-0 border-t px-4 py-4 sm:px-6">
            <Button type="button" variant="outline" onClick={onClose} className="w-full sm:w-auto">
              Cancel
            </Button>
            <Button type="submit" className="w-full sm:w-auto">Create Teacher</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default CreateTeacherModal;
